import React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';
import { Info, CheckCircle, AlertTriangle, XCircle, X } from 'lucide-react';

const alertVariants = cva(
  'relative w-full rounded-lg border p-4',
  {
    variants: {
      variant: {
        default: 'bg-background text-foreground border-border',
        info: 'bg-info-50 text-info-900 border-info-200',
        success: 'bg-success-50 text-success-900 border-success-200',
        warning: 'bg-warning-50 text-warning-900 border-warning-200',
        error: 'bg-error-50 text-error-900 border-error-200',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
);

const iconMap = {
  info: <Info className="h-5 w-5" />,
  success: <CheckCircle className="h-5 w-5" />,
  warning: <AlertTriangle className="h-5 w-5" />,
  error: <XCircle className="h-5 w-5" />,
};

export interface AlertProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof alertVariants> {
  title?: string;
  icon?: React.ReactNode | boolean;
  onClose?: () => void;
}

export function Alert({
  className,
  variant = 'default',
  title,
  icon,
  onClose,
  children,
  ...props
}: AlertProps) {
  const showIcon = icon !== false && (icon || variant !== 'default');
  const iconElement = icon === true || icon === undefined ? iconMap[variant as keyof typeof iconMap] : icon;

  return (
    <div
      role="alert"
      className={cn(alertVariants({ variant }), className)}
      {...props}
    >
      <div className="flex">
        {showIcon && iconElement && (
          <div className="flex-shrink-0">{iconElement}</div>
        )}
        <div className={cn('flex-1', showIcon && 'ml-3')}>
          {title && (
            <h3 className="text-sm font-medium mb-1">{title}</h3>
          )}
          <div className="text-sm">{children}</div>
        </div>
        {onClose && (
          <button
            onClick={onClose}
            className="flex-shrink-0 ml-4 inline-flex text-current opacity-70 hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 rounded"
          >
            <span className="sr-only">Close</span>
            <X className="h-5 w-5" />
          </button>
        )}
      </div>
    </div>
  );
}
